export const SKILL_TITLE_MIN = 2;
export const SKILL_TITLE_MAX = 80;
export const SKILL_DESC_MIN = 5;
export const SKILL_DESC_MAX = 200;
export const SKILL_MARKDOWN_MAX_BYTES = 100 * 1024;
export const SKILL_TOTAL_MAX_BYTES = 8 * 1024 * 1024;
export const SKILL_COMPANION_MAX_FILES = 20;
/**
 * The org-catalog write body ceiling. Mirrors the Gateway's
 * `SKILL_CATALOG_WRITE_MAX_BYTES`: base64 inflates an 8 MiB decoded file to
 * ~10.7 MiB, and this bounds the whole JSON request.
 */
export const SKILL_CATALOG_BODY_MAX_BYTES = 12 * 1024 * 1024;

import { z } from "zod";
import { FileContentSchema, OkDataSchema, UploadFileSchema } from "./common.js";
import {
  AgentIdSchema,
  IdSchema,
  IsoDateTimeSchema,
  JsonObjectSchema,
  RelativePathSchema,
} from "./primitives.js";

const SkillParamTypeSchema = z.enum(["text", "textarea", "select"]);

const SkillParamSchema = z
  .object({
    key: z.string().min(1),
    label: z.string().min(1),
    type: SkillParamTypeSchema,
    required: z.boolean().optional(),
    helperText: z.string().optional(),
    options: z.array(z.string()).optional(),
    default: z.string().optional(),
  })
  .strict();

export const SkillConfigSchema = z.object({ params: z.array(SkillParamSchema) }).strict();

/**
 * The two operator-owned tiers, mirroring the Gateway resolver's vocabulary:
 * `bundled` ships in the image, `catalog` is the org's own. The third tier,
 * `agent-authored`, is never assignable and has its own shape.
 */
const AssignableSkillSourceSchema = z.enum(["bundled", "catalog"]);

const AssignableSkillSchema = z
  .object({
    id: IdSchema,
    source: AssignableSkillSourceSchema.optional(),
    name: z.string().min(1),
    description: z.string(),
    enabled: z.boolean(),
    configSchema: SkillConfigSchema.optional(),
    config: JsonObjectSchema.optional(),
  })
  .strict();

const AgentAuthoredSkillSchema = z
  .object({
    id: IdSchema,
    name: z.string().min(1),
    description: z.string().optional(),
  })
  .strict();

const SkillFileSchema = z
  .object({
    path: RelativePathSchema,
    size: z.number().int().nonnegative(),
    modifiedAt: IsoDateTimeSchema,
  })
  .strict();

const SkillAgentPayloadSchema = z.object({ agentId: AgentIdSchema }).strict();
const SkillIdPayloadSchema = z.object({ agentId: AgentIdSchema, skillId: IdSchema }).strict();
export const SkillConfigurePayloadSchema = z
  .object({ agentId: AgentIdSchema, skillId: IdSchema, config: JsonObjectSchema })
  .strict();
const SkillFilePathPayloadSchema = z
  .object({ agentId: AgentIdSchema, skillId: IdSchema, path: RelativePathSchema })
  .strict();
const SkillFileWritePayloadSchema = z
  .object({
    agentId: AgentIdSchema,
    skillId: IdSchema,
    path: RelativePathSchema,
    content: z.string().optional(),
    encoding: z.enum(["utf8", "base64"]).optional(),
    objectKey: z.string().min(1).optional(),
    overwrite: z.boolean().optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const hasInlineContent = value.content !== undefined;
    const hasObjectKey = value.objectKey !== undefined;
    if (hasInlineContent === hasObjectKey) {
      ctx.addIssue({
        code: "custom",
        message: "skill file write requires exactly one of content or objectKey",
      });
    }
    if (hasObjectKey && value.encoding !== undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["encoding"],
        message: "encoding is only valid for inline content",
      });
    }
  });
export const SkillUploadPayloadSchema = z
  .object({
    agentId: AgentIdSchema,
    name: z.string().min(1),
    description: z.string().optional(),
    files: z.array(UploadFileSchema).min(1),
  })
  .strict();

/**
 * A skill loaded for an agent with no assignment: an active capability — a
 * connected connector type, the agent's memory backend, the authoring tools —
 * or the platform itself, which attaches its handbook to every agent. These are
 * informational on the assignment surface: shown read-only, never toggled.
 */
const CapabilityProvidedSkillSchema = z
  .object({
    id: IdSchema,
    name: z.string().min(1),
    description: z.string(),
    /** What loads this skill — a capability, or the platform for every agent. */
    providedBy: z.enum([
      "connector",
      "memory-backend",
      "dashboard-tools",
      "skill-tools",
      "application-tools",
      "platform",
    ]),
    /** Human-readable source label, e.g. the connector type or backend id. */
    sourceLabel: z.string(),
    /** False when the skill is declared but its skill directory is absent. */
    available: z.boolean(),
  })
  .strict();

/**
 * An advisory expectation mismatch for an assignable skill. The skill
 * declared `expects-tools` / `expects-connectors` the agent does not have.
 * Advisory only — never blocks a load, never auto-grants.
 */
const SkillExpectationWarningSchema = z
  .object({
    skillId: IdSchema,
    missingTools: z.array(z.string()),
    missingConnectors: z.array(z.string()),
  })
  .strict();

export const SkillListPayloadSchema = SkillAgentPayloadSchema;
export const SkillListDataSchema = z
  .object({
    assignable: z.array(AssignableSkillSchema),
    agentAuthored: z.array(AgentAuthoredSkillSchema),
    /**
     * Skills auto-loaded for the agent by an active capability.
     */
    capabilityProvided: z.array(CapabilityProvidedSkillSchema).optional(),
    /**
     * Advisory expectation mismatches for assignable skills.
     */
    expectationWarnings: z.array(SkillExpectationWarningSchema).optional(),
  })
  .strict();
/**
 * Attaching and detaching answer with `changed`, which separates a real
 * assignment from an idempotent no-op (the skill was already attached, or
 * already absent). The bare `OkDataSchema` is `.strict()` and rejected that
 * field, so a successful write parsed as a client-side failure — the mutation
 * reported an error while the change had already landed.
 */
const SkillAssignmentDataSchema = z
  .object({ ok: z.literal(true), changed: z.boolean().optional() })
  .strict();
export const SkillAddPayloadSchema = SkillIdPayloadSchema;
export const SkillAddDataSchema = SkillAssignmentDataSchema;
export const SkillRemovePayloadSchema = SkillIdPayloadSchema;
export const SkillRemoveDataSchema = SkillAssignmentDataSchema;
export const SkillConfigureDataSchema = OkDataSchema;
export const SkillFilesListPayloadSchema = SkillIdPayloadSchema;
export const SkillFilesListDataSchema = z.object({ files: z.array(SkillFileSchema) }).strict();
export const SkillFilesReadPayloadSchema = SkillFilePathPayloadSchema;
export const SkillFilesReadDataSchema = z.object({ file: FileContentSchema }).strict();
export const SkillFilesWritePayloadSchema = SkillFileWritePayloadSchema;
export const SkillFilesWriteDataSchema = z.object({ file: SkillFileSchema }).strict();
export const SkillUploadDataSchema = z.object({ skill: AgentAuthoredSkillSchema }).strict();

export type AssignableSkill = z.infer<typeof AssignableSkillSchema>;
export type AgentAuthoredSkill = z.infer<typeof AgentAuthoredSkillSchema>;
export type CapabilityProvidedSkill = z.infer<typeof CapabilityProvidedSkillSchema>;
export type SkillExpectationWarning = z.infer<typeof SkillExpectationWarningSchema>;
export type SkillListData = z.infer<typeof SkillListDataSchema>;
export type SkillConfig = z.infer<typeof SkillConfigSchema>;
export type SkillFile = z.infer<typeof SkillFileSchema>;

const SkillCompanionFileSchema = z
  .object({
    path: z.string().min(1).max(256),
    // Bounds the base64-inflated string; the decoded-byte total cap and the
    // 12 MiB body limit are the real ceilings.
    content: z.string().max(SKILL_CATALOG_BODY_MAX_BYTES),
    encoding: z.enum(["utf8", "base64"]),
  })
  .strict();

const SkillCompanionPathSchema = z.string().min(1).max(512);

const SkillCatalogEntrySchema = z
  .object({
    id: IdSchema,
    source: AssignableSkillSourceSchema,
    title: z.string().min(SKILL_TITLE_MIN).max(SKILL_TITLE_MAX),
    description: z.string().min(0).max(SKILL_DESC_MAX),
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

const SkillCatalogDetailSchema = SkillCatalogEntrySchema.extend({
  markdown: z.string().min(1).max(SKILL_MARKDOWN_MAX_BYTES),
  companionFiles: z.array(SkillCompanionFileSchema).max(SKILL_COMPANION_MAX_FILES),
}).strict();

export const SkillCatalogListPayloadSchema = z.object({}).strict();
export const SkillCatalogListDataSchema = z
  .object({
    bundled: z.array(SkillCatalogEntrySchema),
    catalog: z.array(SkillCatalogEntrySchema),
  })
  .strict();

export const SkillCatalogReadPayloadSchema = z.object({ skillId: IdSchema }).strict();
export const SkillCatalogReadDataSchema = z.object({ skill: SkillCatalogDetailSchema }).strict();

export const SkillCatalogCreatePayloadSchema = z
  .object({
    title: z.string().min(SKILL_TITLE_MIN).max(SKILL_TITLE_MAX),
    description: z.string().min(SKILL_DESC_MIN).max(SKILL_DESC_MAX),
    markdown: z.string().min(1).max(SKILL_MARKDOWN_MAX_BYTES),
    companionFiles: z.array(SkillCompanionFileSchema).max(SKILL_COMPANION_MAX_FILES).optional(),
    idHint: IdSchema.optional(),
  })
  .strict();
export const SkillCatalogCreateDataSchema = z.object({ skill: SkillCatalogDetailSchema }).strict();

export const SkillCatalogUpdatePayloadSchema = z
  .object({
    skillId: IdSchema,
    title: z.string().min(SKILL_TITLE_MIN).max(SKILL_TITLE_MAX),
    description: z.string().min(SKILL_DESC_MIN).max(SKILL_DESC_MAX),
    markdown: z.string().min(1).max(SKILL_MARKDOWN_MAX_BYTES),
  })
  .strict();
export const SkillCatalogUpdateDataSchema = z.object({ skill: SkillCatalogDetailSchema }).strict();

export const SkillCatalogDeletePayloadSchema = z.object({ skillId: IdSchema }).strict();
export const SkillCatalogDeleteDataSchema = OkDataSchema;

export const SkillCatalogClonePayloadSchema = z.object({ bundledId: IdSchema }).strict();
export const SkillCatalogCloneDataSchema = z.object({ skill: SkillCatalogDetailSchema }).strict();

export const SkillCatalogUploadPayloadSchema = z
  .object({
    skillId: IdSchema,
    files: z.array(SkillCompanionFileSchema).max(SKILL_COMPANION_MAX_FILES).min(1),
  })
  .strict();
export const SkillCatalogUploadDataSchema = z.object({ skill: SkillCatalogDetailSchema }).strict();

export const SkillCatalogDeleteFilePayloadSchema = z
  .object({ skillId: IdSchema, path: SkillCompanionPathSchema })
  .strict();
export const SkillCatalogDeleteFileDataSchema = z
  .object({ skill: SkillCatalogDetailSchema })
  .strict();

export const SkillCatalogMoveFilePayloadSchema = z
  .object({ skillId: IdSchema, from: SkillCompanionPathSchema, to: SkillCompanionPathSchema })
  .strict();
export const SkillCatalogMoveFileDataSchema = z
  .object({ skill: SkillCatalogDetailSchema })
  .strict();

export type AssignableSkillSource = z.infer<typeof AssignableSkillSourceSchema>;
export type SkillCatalogEntry = z.infer<typeof SkillCatalogEntrySchema>;
export type SkillCatalogDetail = z.infer<typeof SkillCatalogDetailSchema>;
export type SkillCatalogListData = z.infer<typeof SkillCatalogListDataSchema>;
